import React, { createContext, useContext } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useLazyLoadQuery } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { UserContext_GetUserByEmailQuery$data } from '@/__generated__/UserContext_GetUserByEmailQuery.graphql'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { UserRole } from '@/schema/graphql'

interface UserData {
  id: string
  email: string
  firstName: string
  lastName: string
  internal: boolean
  role: UserRole
}

interface UserContextProps {
  // userData is the user data for the currently "active" user, which is either the user or - if they are impersonating another user - the impersonated user
  userData: UserData | null
  // loggedInUserData is the user data for the currently logged in user
  loggedInUserData: UserData | null
}

const UserContext = createContext<UserContextProps | undefined>(undefined)

const GetUserByEmailQuery = graphql`
  query UserContext_GetUserByEmailQuery($email: String!) {
    users(where: { email: $email }) {
      edges {
        node {
          id
          email
          firstName
          lastName
          internal
          role
        }
      }
    }
  }
`

/**
 * UserProvider component that fetches and provides user data.
 */
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated } = useAuth0()
  const { impersonatedUser } = useImpersonation()

  const userEmail = isAuthenticated && user?.email ? user.email : ''

  const activeEmail = impersonatedUser ? impersonatedUser.email : userEmail

  const data = useLazyLoadQuery(
    GetUserByEmailQuery,
    { email: activeEmail },
    {
      fetchPolicy: 'store-or-network',
      networkCacheConfig: {
        metadata: {
          skipManagingCustomer: true,
        },
      },
    }
  ) as UserContext_GetUserByEmailQuery$data

  const loggedInData = useLazyLoadQuery(
    GetUserByEmailQuery,
    { email: userEmail },
    {
      fetchPolicy: 'store-or-network',
      networkCacheConfig: {
        metadata: {
          skipImpersonation: true,
          skipManagingCustomer: true,
        },
      },
    }
  ) as UserContext_GetUserByEmailQuery$data

  if (data.users.edges?.length === 0) {
    return null
  }

  const u = data.users.edges?.[0]?.node

  const userData: UserData | null = u
    ? {
        id: u.id,
        email: u.email ?? '',
        firstName: u.firstName ?? '',
        lastName: u.lastName ?? '',
        internal: u.internal ?? false,
        role: u.role ?? UserRole.User,
      }
    : null

  const l = loggedInData.users.edges?.[0]?.node
  const loggedInUserData = l
    ? {
        id: l.id,
        email: l.email ?? '',
        firstName: l.firstName ?? '',
        lastName: l.lastName ?? '',
        internal: l.internal ?? false,
        role: l.role ?? UserRole.User,
      }
    : null

  return (
    <UserContext.Provider value={{ userData, loggedInUserData }}>
      {children}
    </UserContext.Provider>
  )
}

export const useUser = () => {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}
